import type { CheckResult, CheckStatus, ReadinessResponse } from '@goodway/shared';
import { useAttendanceStatus, useSystemStatus } from '../../api/admin';
import { isStale } from '../../api/attendance';
import { useTasksSyncState } from '../../api/tasks';
import { ErrorState, LoadingState } from '../../components/states';
import { usePageTitle } from '../../layout/shellContext';

const STATUS: Record<CheckStatus, { label: string; className: string }> = {
  ok: { label: 'Работает', className: 'status-ok' },
  degraded: { label: 'С замечаниями', className: 'status-info' },
  fail: { label: 'Не работает', className: 'status-bad' },
};

const CHECK_LABEL: Record<keyof ReadinessResponse['checks'], string> = {
  database: 'База данных',
  redis: 'Redis',
  queue: 'Фоновая обработка',
};

const DETAIL_LABEL: Record<string, string> = {
  workerAlive: 'Worker на связи',
  jobsWaiting: 'Заданий ждёт',
  jobsActive: 'Заданий в работе',
  jobsDelayed: 'Заданий отложено',
  jobsFailed: 'Заданий с ошибкой',
  outboxPending: 'Событий в outbox',
  outboxFailed: 'Событий не доставлено',
  outboxOldestPendingSeconds: 'Самое старое событие, с',
};

const formatDetail = (value: string | number | boolean | null) =>
  value === null ? '—' : typeof value === 'boolean' ? (value ? 'да' : 'нет') : String(value);

function CheckCard({ name, check }: { name: keyof ReadinessResponse['checks']; check: CheckResult }) {
  return (
    <article className="panel check-card">
      <div className="panel-header" style={{ marginBottom: 0 }}>
        <h2>{CHECK_LABEL[name]}</h2>
        <span className={`status ${STATUS[check.status].className}`}>{STATUS[check.status].label}</span>
      </div>
      {check.message ? <p className="muted">{check.message}</p> : null}
      <dl>
        {check.latencyMs !== undefined ? (
          <>
            <dt>Время ответа, мс</dt>
            <dd>{check.latencyMs}</dd>
          </>
        ) : null}
        {Object.entries(check.details ?? {}).map(([key, value]) => (
          <div key={key} style={{ display: 'contents' }}>
            <dt>{DETAIL_LABEL[key] ?? key}</dt>
            <dd>{formatDetail(value)}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

const moscow = (value: string | null) =>
  value ? new Date(value).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '—';

/** Загрузка задач Bitrix24 (Ф8): прогресс первичной загрузки, последний запуск, сверка. */
function TasksSyncCard() {
  const sync = useTasksSyncState();
  if (!sync.data) return null;
  const state = sync.data.state;
  const status = !sync.data.configured
    ? { label: 'Не настроена', className: 'status-neutral' }
    : !state
      ? { label: 'Не запускалась', className: 'status-info' }
      : state.lastError
        ? { label: 'Ошибка', className: 'status-bad' }
        : state.initialPhase !== 'done'
          ? { label: 'Первичная загрузка', className: 'status-info' }
          : { label: 'Работает', className: 'status-ok' };
  return (
    <article className="panel check-card">
      <div className="panel-header" style={{ marginBottom: 0 }}>
        <h2>Задачи Bitrix24</h2>
        <span className={`status ${status.className}`}>{status.label}</span>
      </div>
      {state?.lastError ? <p className="muted">{state.lastError}</p> : null}
      {state ? (
        <dl>
          <dt>Загружено при первичной загрузке</dt>
          <dd>{state.initialLoaded}</dd>
          <dt>Первичная загрузка завершена</dt>
          <dd>{moscow(state.initialCompletedAt)}</dd>
          <dt>Последний запуск</dt>
          <dd>{moscow(state.lastRunAt)}</dd>
          <dt>Изменения забираются с</dt>
          <dd>{moscow(state.changesSince)}</dd>
          <dt>Последняя сверка</dt>
          <dd>{state.reconcilePhase ? 'идёт' : moscow(state.lastReconciledAt)}</dd>
        </dl>
      ) : null}
    </article>
  );
}

/** Отчёты системы учёта проходов: когда пришёл последний и кого не удалось связать с сотрудником. */
function AttendanceCard() {
  const status = useAttendanceStatus();
  if (!status.data) return null;
  const { sources, reports, unmatched } = status.data;
  const last = reports[0];
  const state = !sources.length
    ? { label: 'Нет данных', className: 'status-neutral' }
    : !last
      ? { label: 'Отчётов нет', className: 'status-info' }
      : isStale(last.receivedAt)
        ? { label: 'Нет отчётов больше суток', className: 'status-bad' }
        : last.errors
          ? { label: 'Есть ошибки', className: 'status-bad' }
          : { label: 'Работает', className: 'status-ok' };
  return (
    <article className="panel check-card">
      <div className="panel-header" style={{ marginBottom: 0 }}>
        <h2>Посещения</h2>
        <span className={`status ${state.className}`}>{state.label}</span>
      </div>
      {last ? (
        <dl>
          <dt>Последний отчёт</dt>
          <dd>{moscow(last.receivedAt)}</dd>
          <dt>Строк</dt>
          <dd>{last.rows}</dd>
          <dt>Новых / дополнено / без изменений</dt>
          <dd>
            {last.inserted} / {last.updated} / {last.unchanged}
          </dd>
          <dt>Ошибок в строках</dt>
          <dd>{last.errors}</dd>
          <dt>Не связаны с сотрудниками</dt>
          <dd>
            {unmatched.length
              ? unmatched.map((person) => `${person.deviceName} (ID ${person.deviceId})`).join(', ')
              : 'нет'}
          </dd>
        </dl>
      ) : null}
    </article>
  );
}

export function SystemStatusPage() {
  usePageTitle('Состояние системы');
  const status = useSystemStatus();
  if (status.isPending) return <LoadingState label="Проверяю состояние системы" />;
  if (status.isError) {
    return (
      <ErrorState
        title="Не удалось получить состояние системы"
        error={status.error}
        onRetry={() => void status.refetch()}
      />
    );
  }
  return (
    <div className="page">
      <div className="pill-row" style={{ alignItems: 'center' }}>
        <span className={`status ${STATUS[status.data.status].className}`}>
          {STATUS[status.data.status].label}
        </span>
        <span className="muted">
          Версия {status.data.version} · проверено{' '}
          {new Date(status.data.checkedAt).toLocaleTimeString('ru-RU')}
        </span>
      </div>
      <div className="checks">
        {(Object.keys(CHECK_LABEL) as (keyof ReadinessResponse['checks'])[]).map((name) => (
          <CheckCard key={name} name={name} check={status.data.checks[name]} />
        ))}
        <TasksSyncCard />
        <AttendanceCard />
      </div>
    </div>
  );
}
